import {getBaseArticle, getBillingTypeSelection, getCartPaymentSettings, getContracts, getToggleSelections, isAlternateSepaSelection, isBillingAddressOpened, isContactPersonOpened} from "./SimpleSelectors";
import {validateSepa as validateSepaValues} from "../util/validate";
import {createSelector} from "reselect";
import {agencyServerArticleNumber, agencyServerProjectArticles} from "../config/Config";
import {getAgencyServerProjectArticlesInCart} from "./ProjectSelection";
import {isAgencyServer, isAgencyServerCMSArticle, isAgencyServerShopArticle, isHighscaleAgencyServerArticle} from "./ArticleNumbers";
import {APP_MODE_AGENCYSERVER_ADD_PROJECT, APP_MODE_AUTHENTICATED_ORDER, APP_MODE_REGULAR, OUTSOURCING_ADDPROJECT, OUTSOURCING_CREATE_NEW, PAYMENT_TYPE_SEPA} from "../constants/General";
import {isMode} from "./Mode";
import {getAlternateSepaForm, getBillingAddressForm, getCommissionForm, getContactPersonForm, getContractPartnerForm, getProjectSelectionForm, getSepaForm, getSimpleSummaryForm, isNewCustomerSelected, isUserAuthenticated} from "./Forms";
import {getAgencyServerResellers} from "./Resellers";
import {getCurrentProduct, getSelectedArticle} from "./ProductSelection";
import {delve} from "../util/util";
import {_contract} from "./GetParam";
import {getFormSyncErrors} from "redux-form";
import {getAccountExtraStorage, getAccountStorageUsage} from "./Accounts";
import {getSelectedArticleProjectSpace, getSelectedStorageGB} from "./ArticleConfigurator";

// step validationResult selector for whole clientdata step
export const isClientDataStepValid = createSelector(
    [isNewCustomerSelected, isUserAuthenticated, getContractPartnerForm, getBillingAddressForm, getContactPersonForm, getCommissionForm, isBillingAddressOpened, isContactPersonOpened],
    (isNewCustomerSelected, isUserAuthenticated, contractPartnerForm, billingAddressForm, contactPersonForm, commissionForm, billingAddressOpened, contactPersonOpened) => {

        let isValid = true

        if (isNewCustomerSelected) {

            if (contractPartnerForm && (!contractPartnerForm.values || !contractPartnerForm.values.firstName || contractPartnerForm.syncErrors)) {
                isValid = false
            }

            if (billingAddressForm && billingAddressOpened && billingAddressForm.syncErrors) {
                isValid = false
            }

            if(commissionForm && commissionForm.syncErrors) {
                isValid = false
            }

            if (contactPersonForm && contactPersonOpened && contactPersonForm.syncErrors) {
                isValid = false
            }

        } else {

            if (!isUserAuthenticated) {
                isValid = false
            }

        }

        return isValid

    }
)

/**
 * configuration step validator for agency server projects
 */
export const isConfigurationStepValid = createSelector(
    [getBaseArticle, getToggleSelections, getAgencyServerProjectArticlesInCart, getFormSyncErrors("projectSelection")],
    (baseArticleData, toggleSelections, agencyServerProjectsInCart, projectSelectionFormErrors) => {
        const isAgencyServerAddProjectMode = isMode(APP_MODE_AGENCYSERVER_ADD_PROJECT);

        if (!isAgencyServerAddProjectMode && !isAgencyServer(baseArticleData.articleNumber)) {
            if (isMode(APP_MODE_REGULAR) || isMode(APP_MODE_AUTHENTICATED_ORDER)) {
                return (!projectSelectionFormErrors || Object.keys(projectSelectionFormErrors).length === 0);
            } else {
                return true;
            }
        } else {
            // only validate configuration step for agency server and adding projects to agency server
            return agencyServerProjectsInCart.length > 0 && (!projectSelectionFormErrors || Object.keys(projectSelectionFormErrors).length === 0);
        }
    }
);

export const isSimpleSummaryValid = createSelector(
    [
        getSimpleSummaryForm
    ],
    (
        simpleSummaryForm
    ) => {

        let isValid = true

        if (simpleSummaryForm && simpleSummaryForm.syncErrors) {
            isValid = false
        }

        return isValid

    }
)

// step validationResult selector for payment step
export const isPaymentSettingsValid = createSelector(
    [getCartPaymentSettings, getSepaForm, getAlternateSepaForm, getBillingTypeSelection, isAlternateSepaSelection, state => state.clientDataStep.clientData !== false],
    (paymentSettings, sepaForm, alternateSepaForm, billingType, isAlternateSepaSelection, isAuthenticatedUser) => {
        // payment skipped if authenticated user
        if (isAuthenticatedUser) {
            return true
        }
        if (!paymentSettings || !billingType) {
            return false
        }
        if (!('postalInvoice' in paymentSettings)) {
            return false
        }
        if (billingType === PAYMENT_TYPE_SEPA) {
            if (isAlternateSepaSelection && alternateSepaForm && (alternateSepaForm.syncErrors || alternateSepaForm.asyncErrors)) {
                return false
            }
            if (sepaForm && (!sepaForm.values || Object.keys(validateSepaValues(sepaForm.values)).length)) {
                return false
            }
        }
        return true
    }
)

export const isOutsourcingTargetStepValid = createSelector(
    [getToggleSelections, getAgencyServerResellers, getSelectedArticle, getAccountStorageUsage, getSelectedArticleProjectSpace, getAccountExtraStorage, getSelectedStorageGB],
    (toggleSelections, agencyServerResellers, selectedOutsourcingBaseArticle, accountStorageUsage, selectedProjectSpace, accountExtraStorage, selectedStorageGB) => {

        let errors = {};

        if (toggleSelections && toggleSelections["outsourcingTarget"] === OUTSOURCING_CREATE_NEW) {
            if (!selectedOutsourcingBaseArticle) {
                errors['target'] = 'Bitte wähle ein Ziel aus, bevor du fortfährst.'
            }
        } else if (toggleSelections && toggleSelections["outsourcingTarget"] === OUTSOURCING_ADDPROJECT) {
            if (!(toggleSelections && toggleSelections["outsourcingResellerTarget"])) {
                errors['resellerTarget'] = 'Bitte wähle einen Agenturserver als Ziel aus, bevor du fortfährst.'
            }
        } else {
            errors['target'] = 'Bitte wähle ein Ziel aus, bevor du fortfährst.'
        }

        if (accountStorageUsage > selectedProjectSpace + accountExtraStorage + selectedStorageGB) {
            errors['storageException'] = 'Bitte buche weiteren Speicherplatz hinzu, um deinen aktuellen Verbrauch abzudecken.'
        }

        return Object.keys(errors).length === 0 ? true : errors;
    }
)

export const isProductSelectionStepValid = createSelector(
    [getToggleSelections],
    (toggleSelections) => {
        let errors = {};

        if (!toggleSelections["productSelection"]) {
            errors["selection"] = "Bitte wähle eine Option, befor du fortfährst."
        }

        return Object.keys(errors).length === 0 ? true : errors;
    }
)

export const isProductChangeStepValid = createSelector(
    [getSelectedArticle, getCurrentProduct, getContracts, getAgencyServerProjectArticlesInCart, getAccountStorageUsage, getSelectedArticleProjectSpace, getAccountExtraStorage, getSelectedStorageGB, getProjectSelectionForm],
    (selectedArticle, currentProduct, contracts, agencyServerProjectsInCart, accountStorageUsage, selectedProjectSpace, accountExtraStorage, selectedStorageGB, projectForm) => {

        const isAgencyServerArticle = agencyServerProjectArticles.indexOf("" + currentProduct) >= 0

        const contract = contracts[_contract]
        const isLegacyResellerContract = (delve(contract, "sourceType") === "RESELLER" && delve(contract, "article.articleNumber") !== agencyServerArticleNumber);

        let errors = {};

        if (isAgencyServerArticle) {

            if (accountStorageUsage > selectedProjectSpace + accountExtraStorage + selectedStorageGB) {
                errors['storageException'] = 'Bitte buche weiteren Speicherplatz hinzu, um deinen aktuellen Verbrauch abzudecken.'
            }

            if ((!agencyServerProjectsInCart || agencyServerProjectsInCart.length <= 0) && delve(projectForm, "values.article") !== "" + currentProduct) {
                errors['minimumArticles'] = 'Bitte konfiguriere dein Projekt, bevor du fortfährst.'
            }

            if (currentProduct && agencyServerProjectsInCart && agencyServerProjectsInCart.length > 0) {
                agencyServerProjectsInCart.forEach((itemInCart) => {
                    if (
                        (isAgencyServerCMSArticle(itemInCart.articleNumber) || isAgencyServerShopArticle(itemInCart.articleNumber))
                        &&
                        isHighscaleAgencyServerArticle(currentProduct)
                        &&
                        !isHighscaleAgencyServerArticle(itemInCart.articleNumber)
                    ) {
                        errors['downgradeException'] = 'Ein Downgrade in den von dir ausgewählten Tarif ist nicht über das Kundencenter möglich. Wende dich hierfür bitte an den Kundenservice unter +49 (0) 5772/293-100 oder nutze das Ticketsystem im Kundencenter.'
                    }
                });
            }


        } else if (isLegacyResellerContract) {


        } else {

            // check if article is selected
            if (!selectedArticle) {
                errors['selectedArticle'] = 'Bitte wähle einen Tarif, bevor du fortfährst.'
            }
            if (accountStorageUsage > selectedProjectSpace + accountExtraStorage + selectedStorageGB) {
                errors['storageException'] = 'Bitte buche weiteren Speicherplatz hinzu, um deinen aktuellen Verbrauch abzudecken.'
            }

            // check if storage is above usage
        }

        return Object.keys(errors).length === 0 ? true : errors;
    }
)



/** WEBPACK FOOTER **
 ** ./src/selectors/StepValidation.js
 **/